import React from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  PhoneAndroid as MobileIcon,
  Tablet as TabletIcon,
  Computer as DesktopIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { ShareIcon, CopyIcon } from '../shared/Icons';
import { Toast } from '../shared/Toast';
import { DeviceFrame, DeviceId, frameSize } from '../shared/DeviceFrame';
import { useFitScale } from '../../hooks/useFitScale';
import { copyToClipboard } from '../../utils/clipboard';
import './ShareFormDialog.css';

type PreviewDevice = 'desktop' | 'tablet' | 'mobile';

const DEVICE_ID: Record<PreviewDevice, DeviceId> = {
  desktop: 'macbook',
  tablet: 'ipad',
  mobile: 'iphone',
};

const DEVICE_OPTIONS: { id: PreviewDevice; Icon: typeof DesktopIcon }[] = [
  { id: 'desktop', Icon: DesktopIcon },
  { id: 'tablet', Icon: TabletIcon },
  { id: 'mobile', Icon: MobileIcon },
];

interface ShareFormDialogProps {
  open: boolean;
  formName: string;
  shareUrl: string;
  onClose: () => void;
}


export const ShareFormDialog: React.FC<ShareFormDialogProps> = ({
  open,
  formName,
  shareUrl,
  onClose,
}) => {
  const [device, setDevice] = React.useState<PreviewDevice>('desktop');
  const [toast, setToast] = React.useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);


  const embedAttributes: [string, string][] = [
    ['src', shareUrl],
    ['width', '100%'],
    ['height', '800'],
    ['frameborder', '0'],
    ['title', formName || 'Form'],
  ];

  const embedCode = [
    '<iframe',
    ...embedAttributes.map(([name, value]) => `  ${name}="${value}"`),
    '></iframe>',
  ].join('\n');

  const deviceId = DEVICE_ID[device];
  const frame = frameSize(deviceId);
  const { ref: stageRef, scale, measured } = useFitScale({
    contentWidth: frame.width,
    contentHeight: frame.height,
    padding: { x: 48, y: 48 },
  });

 
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (wasOpen.current && !open) setDevice('desktop');
    wasOpen.current = open;
  }, [open]);

  const handleCopy = async (value: string, label: string) => {
    const ok = await copyToClipboard(value);
    setToast({
      message: ok ? `${label} copied to clipboard` : `Could not copy the ${label.toLowerCase()}`,
      severity: ok ? 'success' : 'error',
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{ sx: { overflow: 'hidden' } }}
      >
        <Box className="share-form-shell">
          {/* ---- Left: the link and the embed ---- */}
          <Box className="share-form-panel">
            <Box className="share-form-panel-head">
              <IconButton onClick={onClose} aria-label="close" size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
              <Box className="share-form-panel-divider" />
              <Box className="share-form-head-icon">
                <ShareIcon size={16} />
              </Box>
              <Typography className="share-form-title">Share form</Typography>
            </Box>

            <Box className="share-form-panel-body">
              <Box className="share-form-intro">
                <Typography className="share-form-form-name">
                  {formName || 'Untitled form'}
                </Typography>
                <Typography className="share-form-hint">
                  Anyone with the link below can open this form and submit a
                  response. No account is needed.
                </Typography>
              </Box>

              <Box className="share-form-section">
              <Typography className="share-form-label">Public link</Typography>
              <Box className="share-form-value">{shareUrl}</Box>
              <Box className="share-form-actions">
                <Button
                  variant="contained"
                  disableElevation
                  className="share-form-btn share-form-btn--primary"
                  startIcon={<CopyIcon size={15} />}
                  onClick={() => handleCopy(shareUrl, 'Link')}
                >
                  Copy link
                </Button>
                <Button
                  variant="outlined"
                  className="share-form-btn"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                  onClick={() =>
                    window.open(shareUrl, '_blank', 'noopener,noreferrer')
                  }
                >
                  Open
                </Button>
              </Box>
              </Box>

              <Box className="share-form-section">
              <Typography className="share-form-label">Embed code</Typography>
              <Typography className="share-form-hint">
                Paste this into your own page to host the form inside it.
              </Typography>
              <Box className="share-form-code">
                <Box className="share-form-code-head">
                  <span className="share-form-code-lang">HTML</span>
                  <Tooltip title="Copy embed code">
                    <IconButton
                      size="small"
                      className="share-form-code-copy"
                      onClick={() => handleCopy(embedCode, 'Embed code')}
                    >
                      <CopyIcon size={14} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box component="pre" className="share-form-code-body">
                  <code>
                    <span className="tok-punct">&lt;</span>
                    <span className="tok-tag">iframe</span>
                    {embedAttributes.map(([name, value]) => (
                      <React.Fragment key={name}>
                        {'\n  '}
                        <span className="tok-attr">{name}</span>
                        <span className="tok-punct">=</span>
                        <span className="tok-string">"{value}"</span>
                      </React.Fragment>
                    ))}
                    {'\n'}
                    <span className="tok-punct">&gt;&lt;/</span>
                    <span className="tok-tag">iframe</span>
                    <span className="tok-punct">&gt;</span>
                  </code>
                </Box>
              </Box>
              </Box>
            </Box>
          </Box>

          {/* ---- Right: the form as a recipient sees it ---- */}
          <Box className="share-form-preview">
            <Box className="share-form-preview-head">
              <Typography className="share-form-preview-title">
                Preview
              </Typography>
              <Box className="share-form-device-toggle">
                {DEVICE_OPTIONS.map(({ id, Icon }) => (
                  <Tooltip key={id} title={id}>
                    <IconButton
                      size="small"
                      onClick={() => setDevice(id)}
                      aria-pressed={device === id}
                      className={`share-form-device-btn${
                        device === id ? ' is-active' : ''
                      }`}
                    >
                      <Icon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                ))}
              </Box>
            </Box>

            <Box ref={stageRef} className="share-form-stage">
              <DeviceFrame device={deviceId} scale={scale} hidden={!measured}>
                {/* The live page rather than a re-render of the fields, so what
                    is on screen is exactly what a recipient will open. */}
                {open && (
                  <iframe
                    src={shareUrl}
                    title={`${formName || 'Form'} preview`}
                    className="share-form-iframe"
                  />
                )}
              </DeviceFrame>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <Toast
        open={Boolean(toast)}
        message={toast?.message ?? ''}
        severity={toast?.severity ?? 'success'}
        onClose={() => setToast(null)}
      />
    </>
  );
};
